/**
 * 判定結果を貼り付けられる文章にする。
 *
 * 判定は「この数字でこう判断した」という記録として共有されるが、
 * 画面から手で転記すると、都合のよい数字だけが抜き出されたり、
 * 品質の警告が落ちたりする。落としてはいけないものを落とさない形で
 * 書き出すのがこのモジュールの役割なので、有意でない結果も
 * SRM のような警告も必ず含める。
 */

import {
  blockingReasons,
  verdictOf,
  type AnalysisResult,
  type AnalysisSettings,
  type Issue,
  type MeanAnalysisResult,
  type MeanVariantResult,
  type VariantResult,
} from './stats/analysis'
import { CORRECTION_LABELS } from './stats/multiple'
import { SPENDING_LABELS } from './stats/sequential'
import { alphaFromConfidence } from './stats/proportion'
import type { Tail } from './stats/types'
import {
  formatAlpha,
  formatConfidence,
  formatCount,
  formatMeasure,
  formatInterval,
  formatLift,
  formatPValue,
  formatPercent,
  formatPoints,
  VERDICT_LABELS,
} from './format'

export type ReportFormat = 'markdown' | 'slack'

export const REPORT_FORMAT_LABELS: Record<ReportFormat, string> = {
  markdown: 'Markdown',
  slack: 'Slack',
}

export interface ReportInput {
  analysis: AnalysisResult
  settings: AnalysisSettings
  format: ReportFormat
  /** 同じ画面を再現するリンク。省略すると添えない。 */
  url?: string | undefined
}

const TAIL_LABELS: Record<Tail, string> = {
  'two-sided': '両側',
  greater: '片側 (増加)',
  less: '片側 (減少)',
}

const ISSUE_MARKS: Record<Issue['level'], string> = {
  error: '⛔',
  warning: '⚠️',
  info: 'ℹ️',
}

/** 判定結果を貼り付け用の文章にする。 */
export function buildReport(input: ReportInput): string {
  const { analysis, settings, format, url } = input
  const slack = format === 'slack'
  const lines: string[] = []

  lines.push(heading('A/B テストの判定', 2, slack), '')
  if (analysis.resultsBlocked) {
    lines.push(...blockedNotice(analysis.issues, slack), '')
  }
  lines.push(...observationSection(analysis, slack), '')

  for (const variant of analysis.variants) {
    lines.push(
      ...verdictSection(variant, analysis, settings, slack, analysis.resultsBlocked),
      '',
    )
  }

  lines.push(...settingsSection(analysis, settings, slack), '')

  if (analysis.issues.length > 0) {
    lines.push(...issuesSection(analysis.issues, slack), '')
  }

  if (url) {
    lines.push(slack ? `<${url}|同じ画面を開く>` : `[同じ画面を開く](${url})`)
  }

  // 段落の切れ目で空行が続くと貼り付け先で間延びするため、2 行以上は畳む
  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** 見出し。Slack には見出し記法がないので太字で代用する。 */
function heading(text: string, level: 2 | 3, slack: boolean): string {
  return slack ? `*${text}*` : `${'#'.repeat(level)} ${text}`
}

function bullet(text: string, slack: boolean): string {
  return slack ? `• ${text}` : `- ${text}`
}

function bold(text: string, slack: boolean): string {
  return slack ? `*${text}*` : `**${text}**`
}

/** 観測データ。Markdown は表、Slack は箇条書きにする（Slack に表がないため）。 */
function observationSection(analysis: AnalysisResult, slack: boolean): string[] {
  const rows = [
    { group: analysis.control.group, estimate: analysis.control.estimate },
    ...analysis.variants.map((v) => ({
      group: v.group,
      estimate: v.comparison.variant,
    })),
  ]

  if (slack) {
    return [
      heading('観測データ（先頭が対照群）', 3, slack),
      ...rows.map((row) =>
        bullet(
          `${row.group.name}: ` +
            `${formatCount(row.group.visitors)} 人中 ${formatCount(row.group.conversions)} 件 ` +
            `= ${formatPercent(row.estimate.rate)}`,
          slack,
        ),
      ),
    ]
  }

  const confidenceLabel = formatConfidence(1 - analysis.intervalAlpha)
  return [
    heading('観測データ（先頭が対照群）', 3, slack),
    '',
    `| 群 | 訪問者数 | 成果数 | CVR | ${confidenceLabel} 信頼区間 |`,
    '| --- | ---: | ---: | ---: | --- |',
    ...rows.map((row) =>
      `| ${row.group.name} ` +
      `| ${formatCount(row.group.visitors)} ` +
      `| ${formatCount(row.group.conversions)} ` +
      `| ${formatPercent(row.estimate.rate)} ` +
      `| ${formatInterval(row.estimate.interval, (v) => formatPercent(v))} |`,
    ),
  ]
}

/**
 * 判定を結論として扱えないことの宣言。
 *
 * 貼り付け先で最初に読まれる位置に置く。判定の見出しからも結論を消すので、
 * 都合のよい行だけを切り出しても「有意に改善」だけは持ち出せない。
 */
function blockedNotice(issues: readonly Issue[], slack: boolean): string[] {
  return [
    `> ⛔ ${bold('この結果は判定に使えません', slack)}`,
    `> 理由: ${blockingReasons(issues).join(' / ')}`,
    '> 統計以前にデータが壊れているため、以下の数字を採否の根拠にはできません。' +
      '原因を直して測り直してください。',
  ]
}

/** 比較群 1 つ分の判定。 */
function verdictSection(
  variant: VariantResult,
  analysis: AnalysisResult,
  settings: AnalysisSettings,
  slack: boolean,
  blocked: boolean,
): string[] {
  const { comparison } = variant
  const label = VERDICT_LABELS[verdictOf(variant)]
  const correctionApplied =
    settings.correction !== 'none' && analysis.comparisonCount > 1
  const pValue = correctionApplied ? variant.adjustedPValue : variant.rawPValue

  const lines = [
    heading(
      `${variant.group.name} vs ${analysis.control.group.name}: ` +
        (blocked ? '判定できません（データ品質の問題）' : label),
      3,
      slack,
    ),
    ...(blocked
      ? [
          bullet(
            `データが壊れていなければ「${label}」となる数字ですが、結論としては扱えません。`,
            slack,
          ),
        ]
      : []),
    bullet(
      `絶対差: ${bold(formatPoints(comparison.absoluteDiff), slack)} ` +
        `(${formatInterval(comparison.absoluteDiffInterval, (v) => formatPoints(v))})`,
      slack,
    ),
    bullet(
      `相対リフト: ${bold(formatLift(comparison.relativeLift), slack)} ` +
        `(${formatInterval(comparison.relativeLiftInterval, (v) => formatLift(v))})`,
      slack,
    ),
    bullet(
      `${correctionApplied ? '補正後 p 値' : 'p 値'}: ${formatPValue(pValue)}` +
        `（基準 < ${formatAlpha(analysis.effectiveAlpha)}）` +
        (correctionApplied ? ` / 補正前 ${formatPValue(variant.rawPValue)}` : ''),
      slack,
    ),
  ]

  // 有意でなかったときは、差がないのか人数が足りないのかを必ず添える。
  // ここが落ちると「差がなかった」とだけ伝わってしまう。
  if (!variant.significant) {
    const power = formatPercent(settings.diagnosticPower, 0)
    lines.push(
      bullet(
        variant.mde
          ? `この人数で検出力 ${power} を確保できるのは ` +
              `${formatPoints(Math.abs(variant.mde.absolute))} 以上の差までです` +
              `（観測された差は ${formatPoints(comparison.absoluteDiff)}）`
          : `この人数では検出力 ${power} を確保できる効果量がありません`,
        slack,
      ),
    )
  }

  return lines
}

/** 判定に使った設定。あとから条件を検証できるように必ず添える。 */
function settingsSection(
  analysis: AnalysisResult,
  settings: AnalysisSettings,
  slack: boolean,
): string[] {
  const lines = [
    heading('判定の条件', 3, slack),
    bullet(
      `信頼水準 ${formatPercent(settings.confidence, 0)} / ` +
        `${TAIL_LABELS[settings.tail]} / ` +
        `多重比較補正: ${CORRECTION_LABELS[settings.correction]}` +
        `（比較 ${analysis.comparisonCount} 件）`,
      slack,
    ),
  ]

  if (analysis.sequential) {
    const { settings: sq, current } = analysis.sequential
    lines.push(
      bullet(
        `途中経過の確認: 全 ${sq.looks} 回中 ${sq.currentLook} 回目 ` +
          `(${SPENDING_LABELS[sq.spending]} 型) — ` +
          `この回の基準は p < ${formatAlpha(current.nominalAlpha)}`,
        slack,
      ),
    )
  } else {
    lines.push(
      bullet(
        `判定の基準: p < ${formatAlpha(alphaFromConfidence(settings.confidence))}`,
        slack,
      ),
    )
  }

  return lines
}

/** 品質の警告。都合の悪い指摘こそ落とさない。 */
function issuesSection(issues: readonly Issue[], slack: boolean): string[] {
  return [
    heading('確認事項', 3, slack),
    ...issues.map((issue) =>
      bullet(`${ISSUE_MARKS[issue.level]} ${bold(issue.title, slack)} — ${issue.detail}`, slack),
    ),
  ]
}


/* ============================================================
   連続値の指標（平均の比較）
   ============================================================ */

export interface MeanReportInput {
  analysis: MeanAnalysisResult
  settings: AnalysisSettings
  format: ReportFormat
  url?: string | undefined
}

/** 平均の指標の判定を貼り付け用の文章にする。 */
export function buildMeanReport(input: MeanReportInput): string {
  const { analysis, settings, format, url } = input
  const slack = format === 'slack'
  const lines: string[] = []

  lines.push(heading('A/B テストの判定（平均の指標）', 2, slack), '')
  if (analysis.resultsBlocked) {
    lines.push(...blockedNotice(analysis.issues, slack), '')
  }
  lines.push(...meanObservationSection(analysis, slack), '')

  for (const variant of analysis.variants) {
    lines.push(
      ...meanVerdictSection(variant, analysis, settings, slack, analysis.resultsBlocked),
      '',
    )
  }

  lines.push(...meanSettingsSection(analysis, settings, slack), '')

  if (analysis.issues.length > 0) {
    lines.push(...issuesSection(analysis.issues, slack), '')
  }

  if (url) {
    lines.push(slack ? `<${url}|同じ画面を開く>` : `[同じ画面を開く](${url})`)
  }

  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function meanObservationSection(
  analysis: MeanAnalysisResult,
  slack: boolean,
): string[] {
  const rows = [
    { group: analysis.control.group, estimate: analysis.control.estimate },
    ...analysis.variants.map((v) => ({
      group: v.group,
      estimate: v.comparison.variant,
    })),
  ]

  if (slack) {
    return [
      heading('観測データ（先頭が対照群）', 3, slack),
      ...rows.map((row) =>
        bullet(
          `${row.group.name}: ${formatCount(row.group.n)} 件 / ` +
            `平均 ${formatMeasure(row.group.mean)} / ` +
            `標準偏差 ${formatMeasure(row.group.sd)}`,
          slack,
        ),
      ),
    ]
  }

  const confidenceLabel = formatConfidence(1 - analysis.intervalAlpha)
  return [
    heading('観測データ（先頭が対照群）', 3, slack),
    '',
    `| 群 | 人数 | 平均 | 標準偏差 | ${confidenceLabel} 信頼区間 |`,
    '| --- | ---: | ---: | ---: | --- |',
    ...rows.map(
      (row) =>
        `| ${row.group.name} ` +
        `| ${formatCount(row.group.n)} ` +
        `| ${formatMeasure(row.group.mean)} ` +
        `| ${formatMeasure(row.group.sd)} ` +
        `| ${formatInterval(row.estimate.interval, (v) => formatMeasure(v))} |`,
    ),
  ]
}

function meanVerdictSection(
  variant: MeanVariantResult,
  analysis: MeanAnalysisResult,
  settings: AnalysisSettings,
  slack: boolean,
  blocked: boolean,
): string[] {
  const { comparison } = variant
  const label = VERDICT_LABELS[verdictOf(variant)]
  const correctionApplied =
    settings.correction !== 'none' && analysis.comparisonCount > 1
  const pValue = correctionApplied ? variant.adjustedPValue : variant.rawPValue

  const lines = [
    heading(
      `${variant.group.name} vs ${analysis.control.group.name}: ` +
        (blocked ? '判定できません（データ品質の問題）' : label),
      3,
      slack,
    ),
    ...(blocked
      ? [
          bullet(
            `データが壊れていなければ「${label}」となる数字ですが、結論としては扱えません。`,
            slack,
          ),
        ]
      : []),
    bullet(
      `絶対差: ${bold(formatMeasure(comparison.absoluteDiff, true), slack)} ` +
        `(${formatInterval(comparison.absoluteDiffInterval, (v) => formatMeasure(v, true))})`,
      slack,
    ),
    bullet(
      `相対リフト: ${bold(formatLift(comparison.relativeLift), slack)} ` +
        `(${formatInterval(comparison.relativeLiftInterval, (v) => formatLift(v))})`,
      slack,
    ),
    bullet(
      `${correctionApplied ? '補正後 p 値' : 'p 値'}: ${formatPValue(pValue)}` +
        `（基準 < ${formatAlpha(analysis.effectiveAlpha)}）` +
        (correctionApplied ? ` / 補正前 ${formatPValue(variant.rawPValue)}` : ''),
      slack,
    ),
    bullet(
      `Welch の t 検定: t = ${comparison.t === null ? '—' : comparison.t.toFixed(3)}` +
        ` / 自由度 ${Number.isFinite(comparison.df) ? comparison.df.toFixed(1) : '—'}`,
      slack,
    ),
  ]

  if (!variant.significant) {
    const power = formatPercent(settings.diagnosticPower, 0)
    lines.push(
      bullet(
        variant.mde
          ? `この人数で検出力 ${power} を確保できるのは ` +
              `${formatMeasure(variant.mde)} 以上の差までです` +
              `（観測された差は ${formatMeasure(comparison.absoluteDiff, true)}）`
          : `この人数とばらつきでは検出力 ${power} を確保できる差を計算できません`,
        slack,
      ),
    )
  }

  return lines
}

function meanSettingsSection(
  analysis: MeanAnalysisResult,
  settings: AnalysisSettings,
  slack: boolean,
): string[] {
  const lines = [
    heading('判定の条件', 3, slack),
    bullet(
      `信頼水準 ${formatPercent(settings.confidence, 0)} / ` +
        `${TAIL_LABELS[settings.tail]} / ` +
        `多重比較補正: ${CORRECTION_LABELS[settings.correction]}` +
        `（比較 ${analysis.comparisonCount} 件）`,
      slack,
    ),
    bullet('検定: 等分散を仮定しない Welch の t 検定', slack),
  ]

  if (analysis.sequential) {
    const { settings: sq, current } = analysis.sequential
    lines.push(
      bullet(
        `途中経過の確認: 全 ${sq.looks} 回中 ${sq.currentLook} 回目 ` +
          `(${SPENDING_LABELS[sq.spending]} 型) — ` +
          `この回の基準は p < ${formatAlpha(current.nominalAlpha)}`,
        slack,
      ),
    )
  } else {
    lines.push(
      bullet(
        `判定の基準: p < ${formatAlpha(alphaFromConfidence(settings.confidence))}`,
        slack,
      ),
    )
  }

  return lines
}
